import { useEffect, useState } from "react";
import api from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Calendar, Download } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    api.get("/reports/summary", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const setPreset = (days) => {
    if (days === 0) { setFrom(""); setTo(""); return; }
    setFrom(daysAgoISO(days - 1));
    setTo(todayISO());
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Telecaller","Total","Completed","Hold","Follow-up","Rejected","Not Connected","Revenue (INR)","Conversion %"]];
    data.per_telecaller.forEach(r => rows.push([r.name, r.total, r.completed, r.hold, r.follow_up, r.rejected, r.not_connected, r.sales, r.conversion]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `report-${from || "all"}-to-${to || "today"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  const salesRows = data.per_telecaller.map(t => ({ name: t.name, sales: t.sales, completed: t.completed }));

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-500">Reports</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-black">Sales & Team Report</h1>
          <div className="mt-1 text-xs text-zinc-500">{from || "All time"} → {to || "today"} · {data.total_leads} leads · ₹{data.total_sales.toLocaleString("en-IN")} revenue</div>
        </div>
        <button data-testid="report-export-csv" onClick={exportCsv}
          className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:bg-zinc-50">
          <Download className="h-3.5 w-3.5"/> Export CSV
        </button>
      </div>

      {/* Date filter toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-2 rounded-sm border border-zinc-200 bg-white p-3">
        <Calendar className="h-4 w-4 text-zinc-400"/>
        <label className="text-[11px] uppercase tracking-wider text-zinc-500">From</label>
        <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="rounded-sm border border-zinc-300 px-2 py-1.5 text-xs outline-none focus:border-black" />
        <label className="text-[11px] uppercase tracking-wider text-zinc-500">To</label>
        <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="rounded-sm border border-zinc-300 px-2 py-1.5 text-xs outline-none focus:border-black" />
        <div className="ml-2 flex gap-1">
          {[{l:"Today",d:1},{l:"7d",d:7},{l:"30d",d:30},{l:"90d",d:90},{l:"All",d:0}].map(p => (
            <button key={p.l} data-testid={`report-preset-${p.l.toLowerCase()}`} onClick={() => setPreset(p.d)}
              className="rounded-sm border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-medium hover:bg-zinc-100">{p.l}</button>
          ))}
        </div>
      </div>

      <div className="rounded-sm border border-zinc-200 bg-white p-5 mb-6">
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Revenue by telecaller</div>
          <div className="font-heading text-lg font-bold text-black">Sales generated (₹)</div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={salesRows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525B" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#52525B" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
            <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} formatter={(v) => `₹${v.toLocaleString("en-IN")}`} />
            <Bar dataKey="sales" radius={[2, 2, 0, 0]} fill="#16A34A" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-sm border border-zinc-200 bg-white">
        <div className="border-b border-zinc-200 px-5 py-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Detail</div>
          <div className="font-heading text-lg font-bold text-black">Full telecaller breakdown</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-3">Telecaller</th>
                <th className="px-3 py-3">Total</th>
                <th className="px-3 py-3">Completed</th>
                <th className="px-3 py-3">Hold</th>
                <th className="px-3 py-3">Follow-up</th>
                <th className="px-3 py-3">Rejected</th>
                <th className="px-3 py-3">Not connected</th>
                <th className="px-3 py-3">Revenue</th>
                <th className="px-5 py-3">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {data.per_telecaller.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-zinc-500">No assigned leads yet.</td></tr>}
              {data.per_telecaller.map(r => (
                <tr key={r.telecaller_id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-3 font-medium text-black">{r.name}</td>
                  <td className="px-3 py-3 tabular-nums">{r.total}</td>
                  <td className="px-3 py-3 tabular-nums text-emerald-700">{r.completed}</td>
                  <td className="px-3 py-3 tabular-nums text-amber-700">{r.hold}</td>
                  <td className="px-3 py-3 tabular-nums text-blue-700">{r.follow_up}</td>
                  <td className="px-3 py-3 tabular-nums text-red-700">{r.rejected}</td>
                  <td className="px-3 py-3 tabular-nums text-zinc-700">{r.not_connected}</td>
                  <td className="px-3 py-3 tabular-nums font-semibold">₹{r.sales.toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3 tabular-nums">{r.conversion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
